import { TimestampSlot } from "@/types/pro-availability";
import dayjs from "dayjs";
import { isBetween } from "@/utils/dates";

export interface DayHours {
  label: string;
  value: number;
  firstHalf?: boolean;
  secondHalf?: boolean;
}

export interface AvailableHoursPerDay {
  [date: string]: DayHours[];
}

export interface CalendarAvailability {
  availableDates: {
    start: Date;
    end: Date;
  };
  disabledDates: Date[];
  availableHoursPerDay: AvailableHoursPerDay;
}

export const parseCalendarAvailability = (
  availableDates: {
    start: Date;
    end: Date;
  },
  v: TimestampSlot[],
  timezone: string,
  forPro = false,
): CalendarAvailability => {
  const availableStart = new Date(v[0]?.start);
  const availableEnd = new Date(v[v.length - 1]?.end);

  const interval = 30 * 60 * 1000;
  const availableHoursPerDay: AvailableHoursPerDay = {};
  const disabledDates: Date[] = [];

  for (
    let i = new Date(
      availableStart.getFullYear(),
      availableStart.getMonth(),
      availableStart.getDate(),
    ).getTime();
    i <= availableEnd.getTime();
    i += interval
  ) {
    const dateI = new Date(i);
    const day = dayjs(i).tz(timezone).format("MM/DD/YYYY");
    if (!availableHoursPerDay[day]) availableHoursPerDay[day] = [];

    if (
      v.some(
        (value) =>
          isBetween(i, value.start, value.end) &&
          isBetween(i + interval, value.start, value.end),
      )
    ) {
      const startDate = dayjs(dateI).tz(timezone);
      const endDate = startDate.add(forPro ? 30 : 60, "minutes");
      const hourStart = startDate.format(forPro ? "h:mm A" : "h");
      const hourEnd = endDate.format("h");
      const minutesStart = dayjs(startDate).format("mm");
      const whatM = dayjs(dateI).tz(timezone).format("A");
      availableHoursPerDay[day].push({
        label: forPro ? hourStart : `${hourStart}-${hourEnd} ${whatM}`,
        value: i,
        firstHalf: minutesStart === "00",
        secondHalf: minutesStart === "30",
      });
    }
  }

  if (availableDates) {
    const startDate = new Date(availableDates.start!);
    const endDate = new Date(availableDates.end!);

    const startPeriod = startDate.getTime();
    const endPeriod = endDate.getTime();

    for (let i = startPeriod; i <= endPeriod; i += 86400000) {
      const dateI = new Date(i);
      const day = dayjs(i).format("MM/DD/YYYY");

      if (!availableHoursPerDay[day]?.length) {
        disabledDates.push(dateI);
      }
    }
  }
  if (!forPro) {
    const dayKeys = Object.keys(availableHoursPerDay);
    for (const dayKey of dayKeys) {
      const day = availableHoursPerDay[dayKey];
      for (let index = 0; index < day.length; index++) {
        if (day[index + 1] && day[index + 1].label === day[index].label) {
          day[index].firstHalf = true;
          day[index].secondHalf = true;
          availableHoursPerDay[dayKey].splice(index + 1, 1);
        }
      }
    }
  }
  return {
    availableDates,
    disabledDates,
    availableHoursPerDay,
  };
};
